/**
 * OSMDSightReadingScene
 *
 * A Phaser scene that renders MusicXML using OSMD with the custom Phaser backend.
 * This replaces the SVG-based rendering in SightReadingPhaser.ts.
 *
 * Key differences from SightReadingPhaser:
 * - Uses MusicXML directly instead of pre-rendered SVGs and JSON data
 * - Renders music notation using OSMD's Phaser backend (PhaserVexFlowBackend)
 * - Uses NoteContainer instances for interactive note elements
 * - Maintains cursor logic and note coloring for correct/incorrect playing
 */

import Phaser from 'phaser';
import {
  OpenSheetMusicDisplay,
  BackendType,
  EngravingRules,
  GraphicalMusicPage,
  VexFlowGraphicalNote,
} from '@museflow-ai/opensheetmusicdisplay';
import { PhaserVexFlowBackend } from '../../vexflow-phaser-backend/PhaserVexFlowBackend';
import { NoteContainer, NoteState } from '../../vexflow-phaser-backend/NoteContainer';
import { EventStreamPhaser2, IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import CursorMobile from 'Phaser/GameObjects/CursorMobile';
import {
  OSMDIteratorExtensible,
  IOSMDNotePositionProvider,
  OSMDNoteData,
} from 'Phaser/GameObjects/OSMDIterator';
import { OSMDEventStream, INoteContainerProvider } from 'Phaser/GameObjects/OSMDEventStream';
import {
  ReactToPhaserListeners,
  NotifyReact,
  PhaserListenForReact,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import Phrase from 'Models/Phrase';
import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import * as Tone from 'tone';
import Logger from 'Utils/Logger';
import { isCapacitorNative } from 'Utils/Constants';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import TimeSignature from 'Models/TimeSignature';
import { INoteGraphicsDatabase } from './StaffExercise';
import IPlayable from 'Phaser/GameObjects/Playable';
import ChevronManager from 'Phaser/Gameplay/ChevronManager';
import { notifyAppAudioOutput } from 'AudioRecognition/appAudioSuppression';

// Re-export OSMDNoteData for external use
export type { OSMDNoteData } from 'Phaser/GameObjects/OSMDIterator';

/**
 * Data structure for OSMD phrase rendering.
 * This replaces the PhraseData structure used in SightReadingPhaser.
 */
export interface OSMDPhraseData {
  /** The raw MusicXML string */
  musicXml: string;
  /** Unique identifier for this phrase */
  uuid: string;
  /** Starting timestamp in measure units */
  startTimestamp: number;
  /** Time signature for this phrase */
  timeSignature: TimeSignature;
  /** Whether to show the clef */
  showClef: boolean;
  /** Whether to show the brace */
  showBrace: boolean;
  /** Whether to show the time signature */
  showTimeSig: boolean;
}

/**
 * Represents a playable phrase with OSMD rendering.
 * Implements IOSMDNotePositionProvider for cursor positioning.
 */
class OSMDPlayablePhrase implements IOSMDNotePositionProvider {
  cursor?: CursorMobile;
  osmd: OpenSheetMusicDisplay | null = null;
  phaserBackend: PhaserVexFlowBackend | null = null;
  container: Phaser.GameObjects.Container;
  noteContainers: Map<string, NoteContainer> = new Map();
  notePositions: OSMDNoteData[] = [];
  data: OSMDPhraseData;
  idx: number = 0;
  isActive: boolean = false;
  phraseStartingMeasure: number = 0;
  bottom: boolean = false;

  private scene: Phaser.Scene;
  private dummyContainer: HTMLDivElement | null = null;

  /** Original unscaled width of the rendered content (set after initialize) */
  private originalWidth: number = 0;
  /** Original unscaled height of the rendered content (set after initialize) */
  private originalHeight: number = 0;

  /** Scene height for responsive spacing */
  private sceneHeight: number;

  constructor(
    scene: Phaser.Scene,
    data: OSMDPhraseData,
    x: number,
    y: number,
    bottom: boolean = false,
    sceneHeight: number = 900,
  ) {
    this.scene = scene;
    this.data = data;
    this.bottom = bottom;
    this.sceneHeight = sceneHeight;
    this.container = scene.add.container(x, y);
  }

  async initialize(): Promise<void> {
    // Create a hidden DOM container for OSMD layout calculations
    this.dummyContainer = document.createElement('div');
    this.dummyContainer.id = `osmd-dummy-${this.data.uuid}`;
    this.dummyContainer.style.position = 'fixed';
    this.dummyContainer.style.left = '-99999px';
    this.dummyContainer.style.top = '-99999px';
    this.dummyContainer.style.width = '900px'; // Reduced from 1600px for more compact layout
    this.dummyContainer.style.height = '400px';
    this.dummyContainer.style.overflow = 'hidden';
    this.dummyContainer.style.visibility = 'hidden';
    document.body.appendChild(this.dummyContainer);

    // Create OSMD instance with Phaser backend
    this.osmd = new OpenSheetMusicDisplay(this.dummyContainer, {
      backend: 'phaser',
      autoResize: false,
      pageFormat: 'Endless',
      drawTitle: false,
      drawSubtitle: false,
      drawComposer: false,
      drawLyricist: false,
      drawCredits: false,
      drawPartNames: false,
      drawMeasureNumbers: false,
      drawFingerings: true,
      drawTimeSignatures: this.data.showTimeSig,
      drawMetronomeMarks: false,
      stretchLastSystemLine: true,
      disableCursor: false,
      newPageFromXML: true,
      defaultFontFamily: 'Times New Roman, Canela, Arial',
    });

    // Configure EngravingRules for larger, more prominent notation
    this.osmd.EngravingRules.DefaultVexFlowNoteFont = 'gonville';

    // Staff height controls the spacing between the 5 lines of each staff
    // Default is 4.0 (40px), using 6.0 (60px) for tighter spacing
    this.osmd.EngravingRules.StaffHeight = 6.0;

    // Adjust spacing for the staff height - keep treble and bass close together
    this.osmd.EngravingRules.BetweenStaffDistance = 3.0; // Gap between treble and bass staves
    this.osmd.EngravingRules.StaffDistance = 4.0;
    this.osmd.EngravingRules.BetweenStaffLinesDistance = 1.0;

    // Line widths (scale up for larger notation)
    this.osmd.EngravingRules.StaffLineWidth = 0.1;
    this.osmd.EngravingRules.StemWidth = 0.12;
    this.osmd.EngravingRules.LedgerLineWidth = 1.0;
    this.osmd.EngravingRules.WedgeLineWidth = 0.1;

    // Compact horizontal spacing - make staff narrower
    this.osmd.EngravingRules.SystemLeftMargin = 0;
    this.osmd.EngravingRules.SystemRightMargin = 0;
    this.osmd.EngravingRules.PageLeftMargin = 1;
    this.osmd.EngravingRules.PageRightMargin = 1;
    this.osmd.EngravingRules.PageTopMargin = 1;
    this.osmd.EngravingRules.PageBottomMargin = 1;
    this.osmd.EngravingRules.MeasureLeftMargin = 0.2;
    this.osmd.EngravingRules.MeasureRightMargin = 0.2;

    // Reduce horizontal note spacing to make staff narrower
    this.osmd.EngravingRules.VoiceSpacingMultiplierVexflow = 0.45;
    this.osmd.EngravingRules.VoiceSpacingAddendVexflow = 1.5;

    this.osmd.EngravingRules.SheetMaximumWidth = 32767;
    this.osmd.EngravingRules.NewSystemAtXMLNewSystemAttribute = true;
    this.osmd.DrawingParameters.drawSelectionEndSymbol = true;

    // Set up the Phaser backend factory
    const scene = this.scene;
    const parentContainer = this.container;
    const _noteContainersMap = this.noteContainers;
    const _skipClef = !this.data.showClef;

    this.osmd.phaserBackendFactory = (
      osmdRules: EngravingRules,
      page: GraphicalMusicPage,
    ): PhaserVexFlowBackend => {
      const backend = new PhaserVexFlowBackend(osmdRules, scene);
      backend.graphicalMusicPage = page;

      // Always skip OSMD's default clef rendering - we'll use custom clef images
      backend.getPhaserContext().setSkipClefRendering(true);

      // Enable container routing for interactive elements
      backend.getPhaserContext().setContainerRouting(true);

      // Add the backend's container to our parent container
      parentContainer.add(backend.getPhaserContext().getContainer());

      return backend;
    };

    // Load and render the MusicXML
    await this.osmd.load(this.data.musicXml);
    this.osmd.render();

    // Extract note positions and store NoteContainers
    this.extractNotePositions();
    this.collectNoteContainers();

    // Render custom clefs and brace to replace OSMD's default glyphs
    if (this.data.showClef) {
      this.renderCustomClefs();
    }
    if (this.data.showBrace) {
      this.renderCustomBrace();
    }

    // Store the original unscaled dimensions after rendering
    // These are used by bestFit to calculate proper scaling
    this.originalWidth = this.calculateOriginalWidth();
    this.originalHeight = this.calculateOriginalHeight();
  }

  /**
   * Calculate the original width from OSMD's GraphicSheet.
   */
  private calculateOriginalWidth(): number {
    if (this.osmd?.GraphicSheet) {
      const unitInPixels = 10;
      const pages = this.osmd.GraphicSheet.MusicPages;
      if (pages.length > 0) {
        let maxX = 0;
        for (const page of pages) {
          for (const system of page.MusicSystems) {
            for (const staffLine of system.StaffLines) {
              const measures = staffLine.Measures;
              if (measures.length > 0) {
                const lastMeasure = measures[measures.length - 1];
                const pos = lastMeasure.PositionAndShape;
                const measureEnd = (pos.AbsolutePosition.x + pos.Size.width) * unitInPixels;
                maxX = Math.max(maxX, measureEnd);
              }
            }
          }
        }
        return maxX + 40;
      }
    }
    // Fallback to container bounds
    const bounds = this.container.getBounds();
    return bounds.width > 0 ? bounds.width : 800;
  }

  /**
   * Calculate the original height from OSMD's GraphicSheet.
   */
  private calculateOriginalHeight(): number {
    if (this.osmd?.GraphicSheet) {
      const unitInPixels = 10;
      const pages = this.osmd.GraphicSheet.MusicPages;
      if (pages.length > 0) {
        let maxY = 0;
        for (const page of pages) {
          for (const system of page.MusicSystems) {
            for (const staffLine of system.StaffLines) {
              const pos = staffLine.PositionAndShape;
              const staffBottom = (pos.AbsolutePosition.y + pos.Size.height) * unitInPixels;
              maxY = Math.max(maxY, staffBottom);
            }
          }
        }
        return maxY + 40;
      }
    }
    // Fallback to container bounds
    const bounds = this.container.getBounds();
    return bounds.height > 0 ? bounds.height : 200;
  }

  /**
   * Extract note positions from OSMD's GraphicSheet.
   */
  private extractNotePositions(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;
    const phraseStartTimestamp = this.data.startTimestamp;

    // Calculate measure duration from time signature
    // measureDuration is in whole note fractions (e.g., 3/4 time = 0.75)
    const timeSig = this.data.timeSignature;
    const measureDuration = (1 / timeSig.denominator) * timeSig.numerator;

    for (const page of graphicSheet.MusicPages) {
      for (const system of page.MusicSystems) {
        for (const staffLine of system.StaffLines) {
          const staffIndex = staffLine.ParentStaff?.idInMusicSheet ?? 0;

          for (const measure of staffLine.Measures) {
            const measureNumber = measure.MeasureNumber;
            // Calculate the timestamp at the start of this measure
            // measureNumber is 1-based, so subtract 1 to get 0-based index
            const measureStartTimestamp =
              phraseStartTimestamp + (measureNumber - 1) * measureDuration;

            for (const staffEntry of measure.staffEntries) {
              for (const gve of staffEntry.graphicalVoiceEntries) {
                const voiceNumber = gve.parentVoiceEntry?.ParentVoice?.VoiceId ?? 0;

                for (const graphicalNote of gve.notes) {
                  const sourceNote = graphicalNote.sourceNote;
                  const positionAndShape = graphicalNote.PositionAndShape;

                  if (!positionAndShape) continue;

                  const absPos = positionAndShape.AbsolutePosition;
                  const bbox = positionAndShape.BoundingRectangle;

                  let noteName = 'Rest';
                  let midiPitch = 0;

                  if (sourceNote.Pitch) {
                    const pitch = sourceNote.Pitch;
                    const baseNoteName = this.getNoteEnumString(pitch.FundamentalNote);
                    const standardOctave = pitch.Octave + 3;
                    const accidentalStr = this.getAccidentalString(pitch.Accidental);

                    noteName = `${baseNoteName}${accidentalStr}${standardOctave}`;
                    // Use OSMD's getHalfTone() which correctly handles NoteEnum and accidentals
                    // halfTone is MIDI pitch - 12, so add 12 to get actual MIDI pitch
                    midiPitch = pitch.getHalfTone() + 12;
                  }

                  // Get the VexFlow element ID if available
                  // VexFlowGraphicalNote has vfnote: [VF.StemmableNote, number]
                  const vfGraphicalNote = graphicalNote as VexFlowGraphicalNote;
                  const vfNote = vfGraphicalNote.vfnote?.[0];
                  const vexflowId =
                    (vfNote as { attrs?: { id?: string } })?.attrs?.id ||
                    `note-${measureNumber}-${voiceNumber}-${this.notePositions.length}`;

                  // Calculate the timestamp for this note:
                  // measureStartTimestamp + position within the measure
                  const relInMeasure = staffEntry.relInMeasureTimestamp?.RealValue ?? 0;
                  const noteTimestamp = measureStartTimestamp + relInMeasure;

                  const noteData: OSMDNoteData = {
                    x: absPos.x * unitInPixels,
                    y: absPos.y * unitInPixels,
                    width: (bbox?.width ?? 1) * unitInPixels,
                    height: (bbox?.height ?? 1) * unitInPixels,
                    midiPitch,
                    noteName,
                    duration: sourceNote.Length?.RealValue ?? 0,
                    measureNumber,
                    voiceNumber,
                    staffIndex,
                    isRest: sourceNote.isRest(),
                    timestamp: noteTimestamp,
                    vexflowId,
                  };

                  this.notePositions.push(noteData);
                }
              }
            }
          }
        }
      }
    }
  }

  private getNoteEnumString(noteEnum: number): string {
    const noteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    return noteNames[noteEnum % 7] || 'C';
  }

  private getAccidentalString(accidental: number): string {
    switch (accidental) {
      case 1: // SHARP
      case 5: // QUARTERTONESHARP
        return '#';
      case 2: // FLAT
      case 6: // QUARTERTONEFLAT
        return 'b';
      case 3: // DOUBLESHARP
        return '##';
      case 4: // DOUBLEFLAT
        return 'bb';
      default:
        return '';
    }
  }

  private getAccidentalOffset(accidental: number): number {
    switch (accidental) {
      case 1: // SHARP
      case 5: // QUARTERTONESHARP
        return 1;
      case 2: // FLAT
      case 6: // QUARTERTONEFLAT
        return -1;
      case 3: // DOUBLESHARP
        return 2;
      case 4: // DOUBLEFLAT
        return -2;
      default:
        return 0;
    }
  }

  /**
   * Collect NoteContainer instances from the Phaser backend.
   */
  private collectNoteContainers(): void {
    if (!this.osmd?.Drawer?.Backends) return;

    for (const backend of this.osmd.Drawer.Backends) {
      if (backend.getOSMDBackendType() === BackendType.Phaser) {
        const phaserBackend = backend as PhaserVexFlowBackend;
        const containers = phaserBackend.getPhaserContext().getAllElementContainers();

        for (const container of containers) {
          this.noteContainers.set(container.id, container);

          // Set bounds from note position data if available
          const noteData = this.notePositions.find((n) => n.vexflowId === container.id);
          if (noteData) {
            container.setBounds(noteData.x, noteData.y, noteData.width, noteData.height);
          }
        }
      }
    }
  }

  /** Custom clef images */
  private customClefImages: Phaser.GameObjects.Image[] = [];
  /** Custom brace image */
  private customBraceImage: Phaser.GameObjects.Image | null = null;
  /** Clef scaling ratio - adjusted for OSMD coordinate system */
  private readonly clefGapRatio = 1 / 45;

  /**
   * Render custom clef images to replace OSMD's default glyphs.
   */
  private renderCustomClefs(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;

    for (const musicPage of graphicSheet.MusicPages) {
      for (const musicSystem of musicPage.MusicSystems) {
        const staffLinesInSystem = musicSystem.StaffLines;

        for (let staffLineIndex = 0; staffLineIndex < staffLinesInSystem.length; staffLineIndex++) {
          const staffLine = staffLinesInSystem[staffLineIndex];
          const staffBBox = staffLine.PositionAndShape;
          const staffAbsPos = staffBBox.AbsolutePosition;

          const staffX = staffAbsPos.x * unitInPixels;
          const staffY = staffAbsPos.y * unitInPixels;

          // Get pitch line gap from staff height
          const pitchLineGap = (this.osmd.EngravingRules.StaffHeight / 4) * unitInPixels;

          // Determine clef type
          const isGrandStaff = staffLinesInSystem.length >= 2;
          const isBassClef = isGrandStaff && staffLineIndex % 2 === 1;
          const assetKey = isBassClef ? 'bass_clef' : 'treble_clef';

          if (!this.scene.textures.exists(assetKey)) continue;

          // Calculate clef scale
          const clefScale = this.clefGapRatio * pitchLineGap;

          // Create clef image
          const clefImage = this.scene.add.image(0, 0, assetKey);
          clefImage.setScale(clefScale);

          // Apply slight rotation to treble clef
          if (!isBassClef) {
            clefImage.setRotation(-0.05);
          }

          clefImage.setOrigin(0, 0.5);

          // Calculate clef position
          // Staff lines run from line 0 (top) to line 4 (bottom)
          // The clef images have their visual anchor points offset from center
          const clefSymbolMargin = 5;
          let clefY: number;

          // Get the scaled height to calculate proper offsets
          const scaledClefHeight = clefImage.height * clefScale;

          if (isBassClef) {
            // Bass clef: anchor on F line (line 1, 2nd from top)
            // The bass clef's dots should straddle line 1
            // Offset up from center to align dots with the F line
            clefY = staffY + pitchLineGap * 1 - scaledClefHeight * 0.15;
          } else {
            // Treble clef: anchor on G line (line 3, 2nd from bottom)
            // The treble clef's curl should wrap around line 3
            clefY = staffY + pitchLineGap * 3 - scaledClefHeight * 0.15;
          }

          clefImage.setPosition(staffX + clefSymbolMargin, clefY);
          this.container.add(clefImage);
          this.customClefImages.push(clefImage);
        }
      }
    }
  }

  /**
   * Render custom brace image for grand staff.
   */
  private renderCustomBrace(): void {
    if (!this.osmd?.GraphicSheet) return;

    const graphicSheet = this.osmd.GraphicSheet;
    const unitInPixels = 10;

    for (const musicPage of graphicSheet.MusicPages) {
      for (const musicSystem of musicPage.MusicSystems) {
        const staffLines = musicSystem.StaffLines;
        if (staffLines.length < 2) continue;

        const assetKey = 'brace';
        if (!this.scene.textures.exists(assetKey)) continue;

        // Get first (top/treble) and last (bottom/bass) staff positions
        const firstStaff = staffLines[0];
        const lastStaff = staffLines[staffLines.length - 1];

        const pitchLineGap = (this.osmd.EngravingRules.StaffHeight / 4) * unitInPixels;
        const singleStaffHeight = pitchLineGap * 4; // Height of one 5-line staff

        // Calculate Y positions
        const trebleStaffTopY = firstStaff.PositionAndShape.AbsolutePosition.y * unitInPixels;
        const trebleStaffBottomY = trebleStaffTopY + singleStaffHeight;
        const bassStaffTopY = lastStaff.PositionAndShape.AbsolutePosition.y * unitInPixels;
        const bassStaffBottomY = bassStaffTopY + singleStaffHeight;

        // Total height from top of treble staff to bottom of bass staff
        const grandStaffHeight = bassStaffBottomY - trebleStaffTopY;

        // The brace's center/pinch point should be in the gap between staves
        const gapCenterY = (trebleStaffBottomY + bassStaffTopY) / 2;

        // Get X position (left edge of staff)
        const staffX = firstStaff.PositionAndShape.AbsolutePosition.x * unitInPixels;

        // Create brace image
        const braceImage = this.scene.add.image(0, 0, assetKey);

        // Scale brace to fit exactly from top of treble staff to bottom of bass staff
        // The brace image has decorative ends, so we scale it smaller to fit within
        const braceScale = (grandStaffHeight / braceImage.height) * 0.88;
        braceImage.setScale(braceScale);

        // Set origin to right edge, vertically centered
        braceImage.setOrigin(1, 0.5);

        // Position: to the left of staff, centered at the gap between staves
        // Offset up to account for visual weight of brace
        const braceX = staffX - 3;
        const scaledBraceHeight = braceImage.height * braceScale;
        const braceY = gapCenterY - scaledBraceHeight * 0.07;
        braceImage.setPosition(braceX, braceY);

        this.container.add(braceImage);
        this.customBraceImage = braceImage;
      }
    }
  }

  /**
   * Get a NoteContainer by its VexFlow ID.
   */
  getNoteContainer(id: string): NoteContainer | undefined {
    return this.noteContainers.get(id);
  }

  /**
   * Get all note containers of type "stavenote".
   */
  getStaveNoteContainers(): NoteContainer[] {
    const containers: NoteContainer[] = [];
    for (const container of this.noteContainers.values()) {
      if (container.elementType === 'stavenote') {
        containers.push(container);
      }
    }
    return containers;
  }

  /**
   * Get note position coordinates by ID (world coordinates, accounting for scale).
   */
  getNotePositionCoords(id: string): { x: number; y: number } | undefined {
    const noteData = this.notePositions.find((n) => n.vexflowId === id);
    const scale = this.container.scaleX;
    if (noteData) {
      return {
        x: noteData.x * scale + this.container.x,
        y: noteData.y * scale + this.container.y,
      };
    }
    return undefined;
  }

  /**
   * Get the X position of a note by ID (for INoteGraphicsDatabase compatibility).
   */
  getNotePosition(id: string, timestamp?: number, rest?: boolean): number | null {
    const pos = this.getNoteXPosition(id, timestamp, rest);
    return pos !== undefined ? pos : null;
  }

  /**
   * Get a playable note by its ID (for INoteGraphicsDatabase compatibility).
   */
  getNoteById(id: string, _phraseTimestamp?: number): IPlayable | null {
    const container = this.noteContainers.get(id);
    if (container) {
      return new NoteContainerPlayable(container);
    }
    return null;
  }

  /**
   * Get the X position of a note by ID (world coordinates, accounting for scale).
   */
  getNoteXPosition(id: string, timestamp?: number, isRest?: boolean): number | undefined {
    const scale = this.container.scaleX;

    // First try to find by ID
    const noteData = this.notePositions.find((n) => n.vexflowId === id);
    if (noteData) {
      return noteData.x * scale + this.container.x;
    }

    // If not found by ID and timestamp is provided, find by timestamp
    if (timestamp !== undefined) {
      const notesByTimestamp = this.notePositions.filter(
        (n) => Math.abs(n.timestamp - timestamp) < 0.001 && n.isRest === (isRest ?? false),
      );
      if (notesByTimestamp.length > 0) {
        return notesByTimestamp[0].x * scale + this.container.x;
      }
    }

    return undefined;
  }

  /**
   * Get the first note position in the phrase (world coordinates, accounting for scale).
   * Returns the leftmost non-rest note's x position.
   */
  getFirstNotePosition(): number {
    const notes = this.notePositions.filter((n) => !n.isRest);
    const scale = this.container.scaleX;
    if (notes.length > 0) {
      // Find the leftmost note by x position
      const firstNote = notes.reduce((min, n) => (n.x < min.x ? n : min), notes[0]);
      return firstNote.x * scale + this.container.x;
    }
    return this.container.x;
  }

  /**
   * Get the first note position in a specific measure (world coordinates, accounting for scale).
   * @param measureIndex - 0-based local measure index within this phrase
   */
  getFirstNotePositionInMeasure(measureIndex: number): number | undefined {
    // measureIndex is 0-based local, measureNumber in notePositions is 1-based local
    const measureNumber = measureIndex + 1;
    const notes = this.notePositions.filter((n) => n.measureNumber === measureNumber && !n.isRest);
    const scale = this.container.scaleX;
    if (notes.length > 0) {
      return notes[0].x * scale + this.container.x;
    }
    return undefined;
  }

  /**
   * Get the end position of the phrase (world coordinates, accounting for scale).
   * Returns the end of the staff line, not just the last note's position.
   */
  getPhraseEndPosition(): number {
    // Use the scaled width to get the actual end of the staff line
    const scaledWidth = this.getScaledWidth();
    if (scaledWidth > 0) {
      return this.container.x + scaledWidth;
    }
    // Fallback to original width calculation
    const scale = this.container.scaleX;
    const originalWidth = this.originalWidth || this.calculateOriginalWidth();
    return this.container.x + originalWidth * scale;
  }

  /**
   * Get the height of the rendered phrase (returns scaled height if scaled, original otherwise).
   */
  getHeight(): number {
    // If we have a scaled height from bestFit, return it
    if (this._scaledHeight > 0) {
      return this._scaledHeight;
    }
    // Otherwise return the original unscaled height
    return this.originalHeight || this.calculateOriginalHeight();
  }

  /**
   * Get the width of the rendered phrase (returns scaled width if scaled, original otherwise).
   */
  getWidth(): number {
    // If we have a scaled width from bestFit, return it
    if (this._scaledWidth > 0) {
      return this._scaledWidth;
    }
    // Otherwise return the original unscaled width
    return this.originalWidth || this.calculateOriginalWidth();
  }

  /**
   * Check if this phrase contains a given measure.
   * @param measureIndex - The global 0-based measure index
   */
  containsMeasure(measureIndex: number): boolean {
    // Convert global measure index to local measure number (1-based within the phrase)
    const localMeasureNumber = measureIndex - this.phraseStartingMeasure + 1;
    if (localMeasureNumber < 1) return false;
    return this.notePositions.some((n) => n.measureNumber === localMeasureNumber);
  }

  /**
   * Set the position of the phrase container.
   */
  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /**
   * Set the Y position of the phrase container.
   */
  setY(y: number): void {
    this.container.setY(y);
  }

  /**
   * Get the phrase start X position.
   */
  getPhraseStartX(): number {
    return this.container.x;
  }

  /**
   * Get the next phrase position (end of this phrase).
   */
  getNextPosition(): number {
    return this.getPhraseEndPosition();
  }

  activate(unpause?: boolean): void {
    this.isActive = true;
    this.cursor?.enable();
    if (unpause) this.cursor?.unpause();
  }

  deactivate(): void {
    this.isActive = false;
    this.cursor?.disable();
  }

  pause(): void {
    this.cursor?.pause();
  }

  update(): void {
    if (this.isActive) {
      this.cursor?.update();
    }
  }

  /**
   * Best fit the phrase to given dimensions.
   * Scales the OSMD rendering to fit the target dimensions while maintaining aspect ratio.
   * Prioritizes filling the target height to match the old SVG-based rendering proportions.
   */
  bestFit(width: number, height: number, paddingX: number, paddingY: number): void {
    // Use the original unscaled dimensions (calculated after OSMD render)
    const unscaledWidth = this.originalWidth || this.calculateOriginalWidth();
    const unscaledHeight = this.originalHeight || this.calculateOriginalHeight();

    // Calculate scale factors for both dimensions
    const scaleX = width / unscaledWidth;
    const scaleY = height / unscaledHeight;

    // Prioritize height scaling to fill the vertical space like the old SVG system
    // Only use scaleX if it would cause horizontal overflow
    // Use the minimum of width and height scaling to fit within bounds
    // This ensures content doesn't overflow either dimension
    const scale = Math.min(scaleX, scaleY);

    this.bestFitWithScale(scale, width, paddingX, paddingY);
  }

  /**
   * Apply a specific scale factor and center horizontally.
   * Used when a unified scale is calculated across multiple phrases.
   */
  bestFitWithScale(scale: number, targetWidth: number, paddingX: number, paddingY: number): void {
    const unscaledWidth = this.originalWidth || this.calculateOriginalWidth();
    const unscaledHeight = this.originalHeight || this.calculateOriginalHeight();

    this.container.setScale(scale);

    // Store the current phrase dimensions after scaling for cursor positioning
    this._scaledHeight = unscaledHeight * scale;
    this._scaledWidth = unscaledWidth * scale;

    // Center the content horizontally within the available space
    // paddingX is the left edge of the target area, targetWidth is the target width
    const centerX = paddingX + (targetWidth - this._scaledWidth) / 2;
    this.container.setX(centerX);

    // Position the top phrase at the top padding
    if (!this.bottom) {
      this.container.setY(paddingY);
    }
  }

  private _scaledHeight: number = 0;
  private _scaledWidth: number = 0;

  /**
   * Get the scaled height after bestFit has been called.
   */
  getScaledHeight(): number {
    return this._scaledHeight || this.getHeight();
  }

  /**
   * Get the scaled width after bestFit has been called.
   */
  getScaledWidth(): number {
    return this._scaledWidth || this.getWidth();
  }

  /**
   * Get the original unscaled width.
   */
  getOriginalWidth(): number {
    return this.originalWidth;
  }

  /**
   * Get the original unscaled height.
   */
  getOriginalHeight(): number {
    return this.originalHeight;
  }

  /**
   * Transition the phrase (placeholder for animation).
   */
  transition(type: string): void {
    // Placeholder for transition animations
    if (type === 'Fade') {
      this.scene.tweens.add({
        targets: this.container,
        alpha: 1,
        duration: 300,
      });
    }
  }

  /**
   * Transition out the phrase.
   */
  transitionOut(
    callback: () => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Phaser callback scope
    scope: any,
  ): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 300,
      onComplete: callback,
      callbackScope: scope,
    });
  }

  destroy(): void {
    // Clean up OSMD
    if (this.osmd?.Drawer?.Backends) {
      for (const backend of this.osmd.Drawer.Backends) {
        if (backend.getOSMDBackendType() === BackendType.Phaser) {
          backend.clear();
        }
      }
    }

    // Clean up dummy container
    if (this.dummyContainer?.parentNode) {
      this.dummyContainer.parentNode.removeChild(this.dummyContainer);
    }

    // Destroy cursor
    this.cursor?.destroy();

    // Destroy container
    this.container.destroy();

    // Clear references
    this.osmd = null;
    this.phaserBackend = null;
    this.noteContainers.clear();
    this.notePositions = [];
  }
}

/**
 * OSMD-based sight reading scene.
 * Renders MusicXML directly using OSMD's Phaser backend.
 */
export default class OSMDSightReadingScene
  extends Phaser.Scene
  implements INoteGraphicsDatabase, INoteContainerProvider
{
  phrases: (OSMDPlayablePhrase | undefined)[] = [];
  mainIterator!: IteratorExtensible;
  currentCursor: number = 0;
  currentIdx?: number;
  eventStream?: EventStreamPhaser2 | OSMDEventStream;
  log?: Phaser.GameObjects.Text;

  timeKeeperAuxiliary?: ITimeKeeper;

  phrasesSeen: Map<string, number> = new Map();

  clickAudio?: Phaser.Sound.BaseSound;
  tempo: number = 72;

  lastTickTime?: number;
  lastMeasure?: number;

  debugText?: Phaser.GameObjects.Text;

  // Queue for phrase loading
  phraseLoadQueue: {
    phrase: OSMDPhraseData;
    phraseRaw: Phrase;
  }[] = [];

  pauseQueued = false;
  playbackStarted = false;
  shouldUpdate = false;

  measureCount: number = 0;

  mobileView: boolean = false;
  width: number = 0;
  height: number = 0;
  private firstPhrase = true;
  private playButtonOverlap: number = 0;

  topPhrase: number = 0;
  bottomPhrase: number = 1;

  private activePhrase = 0;
  private phrasesTillNextTier = 4;
  private freePlayMode: boolean = false;

  private gameplayStarted: boolean = false;

  private eventsQueue: [number, number][] = [];
  private startPlayingTriggerEventTimestamp?: number;

  private nextPhraseStartTimer?: Phaser.Time.TimerEvent;
  private chevronsAllowed: boolean = false;
  private currentPhraseEligible: boolean = false;
  chevronManager?: ChevronManager;

  constructor() {
    super('OSMDSightReadingScene');
  }

  preload(): void {
    // Load custom clef and brace SVG assets
    const basePath = '/phaser/graphics/';

    this.load.svg('treble_clef', `${basePath}treble_clef.svg`);
    this.load.svg('bass_clef', `${basePath}bass_clef.svg`);
    this.load.svg('brace', `${basePath}brace.svg`);

    // Load time signature numbers (1-9)
    for (let i = 1; i <= 9; i++) {
      this.load.svg(`time_sig_${i}`, `${basePath}time_sig_${i}.svg`);
    }
  }

  create(): void {
    NotifyReactEvents.CurrentSceneReady(this);
    this.events.once('shutdown', this.destroy, this);
  }

  debugLog(text: string): void {
    if (this.debugText) {
      this.debugText.setText(text);
    }
  }

  init({
    eventStream,
    mobile,
    width,
    height,
  }: {
    eventStream: EventStreamPhaser2 | OSMDEventStream;
    mobile?: boolean;
    width: number;
    height: number;
  }): void {
    this.chevronManager = new ChevronManager(eventStream);
    this.width = width;
    this.height = height;
    this.mobileView = mobile ?? false;
    // Debug overlay disabled — re-enable if needed for diagnostics.
    // this.debugText = this.add.text(100, 100, 'DebugLog', {
    //   fontSize: '12px',
    //   color: '#000000',
    // });
    this.timeKeeperAuxiliary = createTimeKeeper();
    this.eventStream = eventStream;
    this.phrases = [];
    this.cameras.main.setBackgroundColor('0xffffff');

    // If using OSMDEventStream, set this scene as the note container provider
    if (eventStream instanceof OSMDEventStream) {
      eventStream.setNoteContainerProvider(this);
    }

    // Set up event listeners
    ReactToPhaserListeners.ResizeWindow((arg) => {
      this.playButtonOverlap = arg.playButtonOverlap || 0;
      this.resize(arg);
    }, this);
    ReactToPhaserListeners.PauseExercise((data) => this.pauseExercise(data), this);
    // OSMDSightReadingScene uses OSMD phrase payload (AddPhrase with OSMDPhraseDataEventPayload)
    ReactToPhaserListeners.AddPhraseOSMD((data) => this.addPhrase(data), this);
    ReactToPhaserListeners.MidiOn((data) => this.noteOn(data), this);
    ReactToPhaserListeners.MidiOff((data) => this.noteOff(data), this);
    ReactToPhaserListeners.SetNoteState((data) => this.setNoteState(data), this);
    ReactToPhaserListeners.PulseNote((data) => this.pulseNote(data), this);
    ReactToPhaserListeners.StartExercise((data) => this.startExercise(data), this);
    ReactToPhaserListeners.ChangeTempo((data) => this.changeTempo(data), this);
    ReactToPhaserListeners.SetCursor((data) => this.setCursor(data), this);
    ReactToPhaserListeners.SetFreePlayMode((data) => this.setFreePlayMode(data), this);
    ReactToPhaserListeners.ResetChevrons(() => this.chevronManager?.reset(), this);
    ReactToPhaserListeners.SetCursorVisible((data) => this.setCursorVisible(data), this);

    if (!this.clickAudio) {
      this.clickAudio = this.sound.add('click');
    }
  }

  setFreePlayMode({ enabled }: { enabled: boolean }): void {
    this.freePlayMode = enabled;
  }

  startExerciseDummy(): void {
    NotifyReactEvents.StartedPlaying(undefined);
  }

  setCursor(_props: { measure?: number }): void {
    if (!this.playbackStarted) return;

    const activePhrase = this.phrases[this.activePhrase];
    if (!activePhrase) return;

    const lastPlayedMeasure = activePhrase.cursor!.getLastPlayedMeasure();

    // Jump back to the last completed measure (lastPlayedMeasure - 1)
    // But don't go below 0
    const measure = Math.max(lastPlayedMeasure - 1, 0);

    Logger.log('[setCursor] Debug:', {
      activePhrase: this.activePhrase,
      lastPlayedMeasure,
      targetMeasure: measure,
    });

    // Find which phrase contains this measure
    const phraseIdx = this.phrases.findIndex((phrase) => phrase?.containsMeasure(measure));

    if (phraseIdx === undefined || phraseIdx === -1) {
      Logger.error("given measure doesn't appear in any of the active phrases!", {
        measure,
        phrases: this.phrases.map((p) => p?.phraseStartingMeasure),
      });
      // Fallback: use the active phrase's first note position
      const phrase = this.phrases[this.activePhrase];
      if (phrase) {
        phrase.activate();
        const firstNotePos = phrase.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0) / 2);
        phrase.cursor?.enable();
      }
    } else {
      const phrase = this.phrases[phraseIdx]!;
      if (phrase !== this.phrases[this.activePhrase]) {
        const current = this.phrases[this.activePhrase]!;
        const firstNotePos = current.getFirstNotePosition();
        current.cursor?.setStart(firstNotePos, current.data.startTimestamp, 0);
        current.cursor?.setX(firstNotePos - current.cursor!.width * 2);
        current?.deactivate();
        this.activePhrase = phraseIdx;
      }
      phrase.activate();
      const localMeasureIndex = measure - phrase.phraseStartingMeasure;
      const firstNote = phrase.getFirstNotePositionInMeasure(localMeasureIndex);
      // Fallback to phrase's first note position if measure note position not found
      const cursorStartPos = firstNote ?? phrase.getFirstNotePosition();
      const numMeasuresIn = localMeasureIndex;
      const timesig = phrase.data.timeSignature;
      const cursor = this.phrases[this.activePhrase]!.cursor!;
      cursor.lastPlayedMeasure = measure - cursor.startingMeasure;
      cursor.setStart(
        cursorStartPos,
        (timesig.numerator / timesig.denominator) * numMeasuresIn,
        measure - cursor.startingMeasure,
      );
    }
  }

  changeTempo({ tempo }: { tempo: number }): void {
    this.tempo = tempo;
    this.timeKeeperAuxiliary!.setBpm(tempo);

    if (!this.playbackStarted) {
      const activePhrase = this.phrases[this.activePhrase];
      activePhrase?.cursor?.blink((60 / tempo) * 1000);
    }
  }

  setCursorVisible = ({ visible }: { visible: boolean }): void => {
    for (const phrase of this.phrases) {
      phrase.cursor?.setRenderHidden(!visible);
    }
  };

  metronomeClick(): void {
    notifyAppAudioOutput({ reason: 'metronome' });
    if (isCapacitorNative()) {
      MuseFlowAudio.playSoundEffect({ name: 'click' });
    } else {
      if (this.clickAudio === undefined) {
        Logger.warn('metronome audio not loaded!');
      }
      this.clickAudio?.stop();
      this.clickAudio?.play();
    }
  }

  startExercise({ tempo }: { tempo?: number }): void {
    if (tempo) {
      this.tempo = tempo;
    }
    this.shouldUpdate = true;
    if (!this.playbackStarted) {
      this.metronomeClick();
      const secondsPerMeasure = (60 / this.tempo) * 4;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      if (!isCapacitorNative()) {
        Tone.start();
      }
      this.timeKeeperAuxiliary!.setTimeSignatures(0, [new TimeSignature(4, 4)]);
      this.timeKeeperAuxiliary!.resetTime();
      this.timeKeeperAuxiliary!.unpause(this.tempo, 0, measureOffset, NoteType._32nd);
      // Set lastTickTime to the current timestamp to prevent the update loop
      // from immediately playing another click while ensuring proper beat spacing
      this.lastTickTime = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      this.phrases[0]!.activate();
      this.phrases[0]!.cursor!.blink((60 / this.tempo) * 1000);
    } else {
      NotifyReactEvents.StartedPlaying(undefined);
      this.phrases[this.activePhrase]?.activate();
      this.phrases[this.activePhrase]?.cursor?.unpause();
    }
  }

  // runs every 'tick'
  tickUpdate(beatDuration: number) {
    if (this.playbackStarted) {
      if (!this.freePlayMode) this.chevronManager?.onTick();
    } else {
      this.metronomeClick();
      this.phrases[this.activePhrase]?.cursor?.blink(beatDuration);
    }
  }

  update(): void {
    /*if (!this.freePlayMode) {
      const accuracy = this.eventStream?.calcAccuracy();
      if (accuracy !== undefined) {
        const activeCursor = this.phrases[this.activePhrase]?.cursor;

        // Phrase-level eligibility:
        // - Starting a phrase with accuracy >= 95% makes it eligible.
        // - Once it dips below 95% at any point, it is no longer eligible
        //   for a chevron reward (but can still show flashing UI later).
        if (accuracy < 95 && this.currentPhraseEligible) {
          this.currentPhraseEligible = false;
        }

        // Chevron flashing (UI) behaviour:
        // - If accuracy crosses above 95% during a phrase, start flashing.
        // - If it dips below 95%, stop flashing.
        if (
          accuracy >= 95 &&
          !this.chevronsAllowed &&
          activeCursor !== undefined &&
          !activeCursor.isFirstPhrase()
        ) {
          this.chevronsAllowed = true;
          NotifyReact(PhaserToReactEvents.SetPhraseEligible({ eligible: true }));
        } else if (accuracy < 95 && this.chevronsAllowed) {
          this.chevronsAllowed = false;
          NotifyReact(PhaserToReactEvents.SetPhraseEligible({ eligible: false }));
        }
      }
    }*/
    if (this.pauseQueued) {
      this._pauseExercise();
    }
    if (this.shouldUpdate) {
      this.phrases.forEach((p) => p?.update());
      const timeKeeper = this.playbackStarted
        ? this.eventStream?.timeKeeper
        : this.timeKeeperAuxiliary;
      if (timeKeeper === undefined) {
        Logger.warn('timekeeper unavailable!');
        return;
      }
      const timestamp = timeKeeper.audioTimeToMeasureTimestamp();
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME;
      }
      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (measureDurationSinceLastTickTime > beatFraction) {
        const beatDuration = (60 / (this.tempo ?? 72)) * 1000;
        this.tickUpdate(beatDuration);
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
      }
    }
  }

  showToast(message: string): void {
    Logger.debug(message);
  }

  onPlayingLastSlice(start: number, duration: number, _durationInSecs: number): void {
    if (!this.mobileView) {
      const nextPhrase = (this.activePhrase + 1) % 4;
      const phrase = this.phrases[nextPhrase];
      if (phrase) {
        // Reset cursor position to the start of the phrase BEFORE making it visible
        // to prevent a flash where the cursor appears at its previous position
        const firstNotePos = phrase.getFirstNotePosition();
        phrase.cursor?.setX(firstNotePos - (phrase.cursor?.width ?? 0));
        phrase.activate();
        phrase.cursor?.startCountingIn(start, duration, duration);
      }
    }
  }

  onFinishPlayingPhrase(_eligible?: boolean): void {
    if (!this.freePlayMode) {
      this.chevronManager?.onPhraseFinishPlaying();
      /*
      // Gain a chevron if the phrase was eligible for the entire phrase
      // (never dipped below 95%) and the final accuracy is still >= 95%.
      if (acc >= 95 && this.currentPhraseEligible) {
        this.phrasesTillNextTier = Math.max(this.phrasesTillNextTier - 1, 0);
      }
      // Lose a chevron if accuracy for the phrase is very low.
      if (acc < 75) {
        this.phrasesTillNextTier = Math.min(this.phrasesTillNextTier + 1, 4);
      }
      NotifyReact(SetPhrasesTillNextTier(this.phrasesTillNextTier));

      // Set up eligibility state for the *next* phrase:
      // when the next phrase starts, its initial eligibility is based on
      // the accuracy at the end of this phrase (>= 95% or not).
      const nextEligible = acc >= 95;
      this.currentPhraseEligible = nextEligible;
      this.chevronsAllowed = nextEligible;
      NotifyReact(SetPhraseEligible(!this.freePlayMode && nextEligible));*/
    }
    if (!this.mobileView) {
      this.phrases[this.activePhrase]?.deactivate();
      this.activePhrase = (this.activePhrase + 1) % 4;
      this.phrases[this.activePhrase]?.activate(true);
    }
  }

  /**
   * Add a phrase using MusicXML data.
   * This is the OSMD version of addPhrase that takes MusicXML instead of SVG/JSON.
   */
  async addPhrase(phraseData: { phraseRaw: Phrase; phrase: OSMDPhraseData }): Promise<void> {
    let phraseToLoad = phraseData;

    // Do not add a phrase if its starting timestamp is earlier than the current phrase
    if (
      this.phrases[this.activePhrase] &&
      phraseToLoad.phrase.startTimestamp <= this.phrases[this.activePhrase]!.cursor!.getTimestamp()
    ) {
      return;
    }

    // Check the load queue
    this.phraseLoadQueue = this.phraseLoadQueue.sort(
      (a, b) => b.phrase.startTimestamp - a.phrase.startTimestamp,
    );
    const inQueue = this.phraseLoadQueue.at(0);
    if (inQueue && inQueue.phrase.startTimestamp < phraseToLoad.phrase.startTimestamp) {
      phraseToLoad = inQueue;
      this.phraseLoadQueue[0] = phraseData;
    }

    const { phrase, phraseRaw } = phraseToLoad;

    Logger.log('[OSMDScene] addPhrase called:', {
      uuid: phrase.uuid,
      startTimestamp: phrase.startTimestamp,
      alreadySeen: this.phrasesSeen.has(phrase.uuid),
      seenTimestamp: this.phrasesSeen.get(phrase.uuid),
      currentIdx: this.currentIdx,
      phrasesLength: this.phrases.length,
    });

    if (phraseToLoad && this.phrasesSeen.get(phrase.uuid) !== phrase.startTimestamp) {
      if (phrase.startTimestamp !== 0 && this.phrases.length === 0) {
        this.phraseLoadQueue.push({ phrase, phraseRaw });
        return;
      }

      // Mark as seen IMMEDIATELY to prevent duplicate processing of the same phrase
      // This must happen before any async operations but AFTER the queue check
      this.phrasesSeen.set(phrase.uuid, phrase.startTimestamp);

      const x = this.mobileView
        ? this.currentIdx !== undefined
          ? this.phrases[this.currentIdx]!.getNextPosition()
          : 100
        : 100;

      // Capture the phrase index BEFORE any async operations to avoid race conditions
      const phraseIdx = this.currentIdx !== undefined ? this.currentIdx : 0;
      const isBottom = phraseIdx % 2 === 1;

      // Update tracking variables
      if (phraseIdx % 2 === 0) {
        this.topPhrase = phraseIdx;
      } else {
        this.bottomPhrase = phraseIdx;
      }

      // Increment currentIdx for the next phrase
      this.currentIdx = (phraseIdx + 1) % 4;

      // Create OSMD phrase data
      const osmdPhraseData: OSMDPhraseData = {
        musicXml: phraseRaw.musicXML.xml,
        uuid: phrase.uuid,
        startTimestamp: phrase.startTimestamp,
        timeSignature: phrase.timeSignature,
        showClef: !this.mobileView || phrase.startTimestamp === 0,
        showBrace: !this.mobileView || phrase.startTimestamp === 0,
        showTimeSig: phrase.startTimestamp === 0,
      };

      // Create the OSMD playable phrase with captured isBottom flag
      const osmdPhrase = new OSMDPlayablePhrase(this, osmdPhraseData, x, 0, isBottom, this.height);

      // Initialize the phrase (async)
      await osmdPhrase.initialize();

      osmdPhrase.phraseStartingMeasure = this.measureCount;
      this.measureCount += osmdPhrase.notePositions.reduce(
        (max, n) => Math.max(max, n.measureNumber),
        0,
      );

      // Use captured phraseIdx for all phrase slot operations
      const outgoingSlot = (phraseIdx + 2) % 4;
      const outgoingPhrase = this.phrases[outgoingSlot];
      outgoingPhrase?.transitionOut(() => {
        if (this.phrases[outgoingSlot] === outgoingPhrase) {
          this.phrases[outgoingSlot]?.destroy();
          this.phrases[outgoingSlot] = undefined;
        }
        this.phrases[phraseIdx]?.transition('Fade');
      }, this);

      const cameraHeight = this.cameras.main.height;
      // Responsive phrase gap - smaller when screen height is less than 900px
      const isCompactHeight = cameraHeight < 900;
      const phraseGap = isCompactHeight
        ? 10 * window.devicePixelRatio
        : 50 * window.devicePixelRatio;
      const phraseHeight = osmdPhrase.getHeight();
      const availVertSpace = phraseHeight * 2 + phraseGap;
      const paddingVert = (cameraHeight - availVertSpace) / 2;
      const phraseY = !this.mobileView
        ? paddingVert + (isBottom ? phraseHeight + phraseGap : 0)
        : this.cameras.main.height / 2 - phraseHeight / 2;

      osmdPhrase.setPosition(x, phraseY);

      // Create cursor - use actual scaled phrase height for proper sizing
      const actualPhraseHeight = osmdPhrase.getScaledHeight();
      const cursorHeight = actualPhraseHeight * 1.35;
      const cursorWidth = cursorHeight / 10;
      const cursorY = actualPhraseHeight / 2 - cursorHeight / 2 + osmdPhrase.container.y;
      const firstNotePosition = osmdPhrase.getFirstNotePosition();

      // Create OSMD-specific iterator for cursor
      const osmdIterator = new OSMDIteratorExtensible(
        osmdPhrase,
        osmdPhrase.notePositions,
        phraseRaw,
        phrase.timeSignature,
        phrase.startTimestamp,
      );

      const phraseCursor = new CursorMobile(
        this,
        firstNotePosition,
        cursorY,
        cursorWidth,
        cursorHeight,
        this.eventStream!,
        osmdIterator,
        this.mobileView,
        this.onPlayingLastSlice,
        this.onFinishPlayingPhrase,
        this,
        phrase.startTimestamp > 0,
        phrase.startTimestamp,
        phraseIdx,
        phraseIdx === 0,
      );

      phraseCursor.reset(
        new OSMDIteratorExtensible(
          osmdPhrase,
          osmdPhrase.notePositions,
          phraseRaw,
          phrase.timeSignature,
          phrase.startTimestamp,
        ),
        phrase.startTimestamp,
        osmdPhrase.phraseStartingMeasure,
      );

      // Set the cursor's initial position to the first note
      // This is important because reset() sets lastNotePos to 0
      phraseCursor.setLastNotePos(firstNotePosition);
      phraseCursor.setX(firstNotePosition - cursorWidth / 2);

      osmdPhrase.cursor = phraseCursor;

      // Disable cursor by default - it will be enabled when the phrase is activated
      // This ensures only the active phrase shows its cursor
      phraseCursor.disable();

      // Store phrase at the captured index
      if (this.phrases[phraseIdx]) {
        this.phrases[phraseIdx]?.destroy();
      }
      this.phrases[phraseIdx] = osmdPhrase;

      Logger.log('[OSMDScene] Phrase stored:', {
        phraseIdx,
        isBottom,
        uuid: phrase.uuid,
        startTimestamp: phrase.startTimestamp,
        phraseY,
      });

      if (!this.mobileView) {
        this.resize({
          width: Math.max(this.width, 960),
          height: this.height,
          playButtonOverlap: this.playButtonOverlap,
        });
      }

      // Note: We do NOT call phraseCursor.addPhrase() here because the iterator
      // was already created with the phrase data in the reset() call above.
      // Calling addPhrase would duplicate the data.

      // Add events to the event stream using the OSMD-derived iterator
      const osmdEventIterator = new OSMDIteratorExtensible(
        osmdPhrase,
        osmdPhrase.notePositions,
        phraseRaw,
        phrase.timeSignature,
        phrase.startTimestamp,
      );
      this.eventStream?.addEvents2(osmdEventIterator, phraseRaw.getStartTimestamp());

      // phrasesSeen is now set at the beginning to prevent race conditions
      this.firstPhrase = false;

      // Process any queued phrases now that we have at least one phrase loaded
      if (this.phraseLoadQueue.length > 0) {
        const queuedPhrase = this.phraseLoadQueue.shift();
        if (queuedPhrase) {
          Logger.log('[OSMDScene] Processing queued phrase:', queuedPhrase.phrase.uuid);
          // Recursively call addPhrase to process the queued phrase
          this.addPhrase(queuedPhrase);
        }
      }
    }
  }

  getNoteById(id: string, _phraseStart?: number): IPlayable | null {
    for (const phrase of this.phrases) {
      if (!phrase) continue;
      const container = phrase.getNoteContainer(id);
      if (container) {
        // Return a wrapper that implements IPlayable
        return new NoteContainerPlayable(container);
      }
    }
    return null;
  }

  /**
   * Get a NoteContainer by its VexFlow ID.
   * Implements INoteContainerProvider interface.
   */
  getNoteContainer(id: string): NoteContainer | undefined {
    for (const phrase of this.phrases) {
      if (!phrase) continue;
      const container = phrase.getNoteContainer(id);
      if (container) {
        return container;
      }
    }
    Logger.warn('[getNoteContainer] Container not found for ID:', id);
    return undefined;
  }

  pauseExercise(): void {
    this.pauseQueued = true;
  }

  _pauseExercise(): void {
    this.shouldUpdate = false;
    this.lastTickTime = undefined;
    this.phrases[this.activePhrase]?.pause();
    const lastPlayedMeasure = Math.max(
      this.phrases[this.activePhrase]!.cursor!.getLastPlayedMeasure() - 1,
      0,
    );
    NotifyReactEvents.ExercisePaused({ lastPlayedMeasure });
    this.pauseQueued = false;
    this.setCursor({});
  }

  getCurrentPhrase(): OSMDPlayablePhrase {
    let currentPhrase: OSMDPlayablePhrase = this.phrases[0]!;
    const currentTimestamp = this.eventStream!.timeKeeper.audioTimeToMeasureTimestamp();
    for (const phrase of this.phrases) {
      if (phrase && phrase.data.startTimestamp > currentTimestamp) continue;
      else if (phrase && phrase.data.startTimestamp > currentPhrase.data.startTimestamp) {
        currentPhrase = phrase;
      }
    }
    return currentPhrase;
  }

  setNoteState({ id, state }: { id: string; state: ProgressState }): void {
    const note = this.getNoteById(id, this.getCurrentPhrase().data.startTimestamp);
    note?.setTimingState(state);
  }

  pulseNote({ id }: { id: string }): void {
    const note = this.getNoteById(id, this.getCurrentPhrase().data.startTimestamp);
    note?.pulse();
  }

  getNotePosition(id: string, timestamp?: number, rest?: boolean): number | null {
    for (const phrase of this.phrases) {
      if (!phrase) continue;
      const x = phrase.getNoteXPosition(id, timestamp, rest);
      if (x !== undefined) {
        return x;
      }
    }
    return null;
  }

  getPhraseEndPosition(): number {
    return Math.max(
      ...this.phrases
        .filter((p) => p !== undefined)
        .map((phrase) => phrase!.getPhraseEndPosition()),
    );
  }

  startPlaying(): void {
    this.timeKeeperAuxiliary?.pause();
    this.timeKeeperAuxiliary?.resetTime();
    this.phrases[0]?.activate();
    this.phrases[0]?.cursor?.unpause(0);
    NotifyReactEvents.StartedPlaying(undefined);
    this.playbackStarted = true;
  }

  noteOn({ note, velocity }: { note: number; velocity?: number }): void {
    if (this.playbackStarted && !this.gameplayStarted) {
      this.eventsQueue.push([note, velocity ?? 0]);
    }
    if (!this.playbackStarted && this.eventStream !== undefined) {
      const timestamp = this.timeKeeperAuxiliary!.audioTimeToMeasureTimestamp();
      if (!this.lastTickTime) return;
      const timeDiff = timestamp - this.lastTickTime;
      if (timeDiff > 0.06 && timeDiff < 0.125) {
        NotifyReactEvents.ShowToast('Too late');
      } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
        NotifyReactEvents.ShowToast('Too early');
      } else if (!this.eventStream.isPitchValidForSightReadingStart(note, timestamp)) {
        // On-beat but wrong pitch — do not start playback.
      } else {
        this.timeKeeperAuxiliary?.pause();
        this.timeKeeperAuxiliary?.resetTime();
        const measureOffset = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
        this.phrases[0]?.activate();
        this.phrases[0]?.cursor?.unpause(measureOffset);
        this.eventsQueue.push([note, velocity ?? 0]);
        NotifyReactEvents.StartedPlaying({
          measureOffset,
          processEventsQueue: [this.processEventsQueue, this],
        });
        this.playbackStarted = true;
      }
    }
  }

  processEventsQueue(): void {
    this.eventsQueue.forEach(([note, velocity]) => {
      if (velocity === 0) this.eventStream?.handleNoteOffEvent(note);
      else this.eventStream?.handleNoteOnEvent(note, velocity);
    });
    this.eventsQueue = [];
  }

  noteOff({ note: _note }: { note: number }): void {}

  destroy(): void {
    this.phrases.forEach((p) => p?.destroy());
    this.phrases = [];
    RemoveListener('SetCursor');
    RemoveListener('AddPhraseOSMD');
    RemoveListener('ResizeWindow');
    RemoveListener('SetFreePlayMode');
    RemoveListener('ResetChevrons');
    this.playbackStarted = false;
    this.shouldUpdate = false;
    this.pauseQueued = false;
  }

  resize(props: { width: number; height: number; playButtonOverlap?: number }): void {
    let width = props.width;
    let height = props.height;
    const isCompactHeight = this.height < 600;
    console.log('[OSMD Resize] this.height:', this.height);
    console.log('[OSMD Resize] isCompactHeight:', isCompactHeight);
    if (width > 960) width = 960;
    if (height > 640) height = 640;
    if (height < 600) height = 600;

    this.width = width;
    this.height = height;
    if (props.playButtonOverlap !== undefined) {
      this.playButtonOverlap = props.playButtonOverlap;
    }
    width *= window.devicePixelRatio;
    height *= window.devicePixelRatio;
    this.scale.resize(width, height);

    if (this.phrases[0] === undefined) return;

    const minAspectRatio = 4.5;
    const maxAspectRatio = 12.0; // Allow wider layouts
    // Use the calculated width/height directly instead of camera dimensions
    // Camera dimensions can be stale or out of sync after resize
    const vpWidth = width;
    const vpHeight = height;

    console.log('[OSMD Resize] Input:', {
      width: props.width,
      height: props.height,
      overlap: props.playButtonOverlap,
    });
    console.log('[OSMD Resize] Viewport:', {
      vpWidth,
      vpHeight,
      playButtonOverlap: this.playButtonOverlap,
    });
    const minPaddingX = 30; // Reduced for more horizontal space
    const minPaddingY = 40; // Reduced for more vertical space
    const maxPaddingX = 60; // Reduced for more horizontal space
    const maxPaddingY = 30; // Reduced for more vertical space

    // Account for play button overlap: subtract overlap from top and bottom symmetrically
    // This reduces the available vertical space
    // const verticalOverlapAdjustment = this.playButtonOverlap;
    // const adjustedVpHeight = vpHeight - (verticalOverlapAdjustment * 2);
    const minScreenSpaceY = vpHeight - maxPaddingY * 2;
    const minScreenSpaceX = vpWidth - maxPaddingX * 2;
    // Responsive gap multiplier for centering calculations
    const totalHeightMultiplier = 2;
    // isCompactHeight ? 2.05 : 2.2;  // 2 phrases + gap (5% or 20%)

    // increase phrase height to use more vertical space - these numbers are trial and error
    const nominalPhraseHeight = minScreenSpaceY * (isCompactHeight ? 0.31 : 0.27);
    const nominalPhraseWidth = minScreenSpaceX;
    const nominalAspectRatio = nominalPhraseWidth / nominalPhraseHeight;
    let phraseHeight = nominalPhraseHeight;
    let phraseWidth = nominalPhraseWidth;

    // Center horizontally
    let paddingX = (vpWidth - phraseWidth) / 2;

    // Calculate total content height and center vertically in the full viewport
    const totalContentHeight = phraseHeight * totalHeightMultiplier;
    let _paddingY = (vpHeight - totalContentHeight) / 2;

    if (nominalAspectRatio < minAspectRatio) {
      phraseWidth = nominalPhraseHeight * 4.5;
      if (phraseWidth > vpWidth - minPaddingX * 2) {
        phraseHeight = nominalPhraseWidth / 4.5;
        const newTotalHeight = phraseHeight * totalHeightMultiplier;
        _paddingY = (vpHeight - newTotalHeight) / 2;
      } else {
        paddingX = (vpWidth - phraseWidth) / 2;
      }
    } else if (nominalAspectRatio > maxAspectRatio) {
      phraseHeight = nominalPhraseWidth / 10.0;
      if (phraseHeight * totalHeightMultiplier > vpHeight - minPaddingY * 2) {
        phraseWidth = nominalPhraseHeight * 10.0;
        paddingX = (vpWidth - phraseWidth) / 2;
      } else {
        const newTotalHeight = phraseHeight * totalHeightMultiplier;
        _paddingY = (vpHeight - newTotalHeight) / 2;
      }
    }

    // Calculate a unified scale factor based on the widest phrase
    // This ensures all phrases have the same width after scaling
    let maxOriginalWidth = 0;
    let maxOriginalHeight = 0;
    this.phrases.forEach((p) => {
      if (p) {
        maxOriginalWidth = Math.max(maxOriginalWidth, p.getOriginalWidth());
        maxOriginalHeight = Math.max(maxOriginalHeight, p.getOriginalHeight());
      }
    });

    // Calculate unified scale using the widest phrase dimensions
    const unifiedScaleX = phraseWidth / maxOriginalWidth;
    const unifiedScaleY = phraseHeight / maxOriginalHeight;
    const unifiedScale = Math.min(unifiedScaleX, unifiedScaleY);

    console.log('[OSMD Resize] Phrase dimensions:', { phraseWidth, phraseHeight });
    console.log('[OSMD Resize] Scale:', { unifiedScaleX, unifiedScaleY, unifiedScale });

    // Apply the scale first (this sets the scaled dimensions)
    this.phrases.forEach(
      (p) => p?.bestFitWithScale(unifiedScale, phraseWidth, paddingX, 0), // Pass 0 for now, we'll recalculate
    );

    // Get the actual scaled height after scaling
    const actualScaledHeight = this.phrases[0]?.getScaledHeight() ?? phraseHeight;

    // Use the gap multiplier from earlier (isCompactHeight already defined above)
    // Smaller gap on larger screens, larger gap on compact screens
    const gapMultiplier = isCompactHeight ? 0.05 : 0.05;
    const gap = actualScaledHeight * gapMultiplier;

    // Calculate actual total content height and center vertically
    const actualTotalContentHeight = actualScaledHeight * 2 + gap;
    const centeredPaddingY = (vpHeight - actualTotalContentHeight) / 2;

    // Reposition all phrases with correct centering
    if (!this.mobileView) {
      this.phrases.forEach((p) => {
        if (!p) return;
        if (p.bottom) {
          p.setY(centeredPaddingY + actualScaledHeight + gap);
        } else {
          p.container.setY(centeredPaddingY);
        }
      });
    }

    this.phrases.forEach((p, _idx) => {
      const c = p?.cursor;
      if (c === undefined || !p) return;
      // Use the actual scaled phrase height for cursor sizing
      const actualPhraseHeight = p.getScaledHeight();
      c.height = actualPhraseHeight * 0.8;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      // Use the phrase's actual Y position to center the cursor
      // Offset upward slightly since staff content may have bottom margin
      const phraseContainerY = p.container.y;
      const cursorY = phraseContainerY + actualPhraseHeight * 0.43 - c.height / 2;
      c.setY(cursorY);
      // Update cursor X position for all cursors when playback hasn't started
      if (!this.playbackStarted) {
        const firstNote = p.getFirstNotePosition();
        if (firstNote !== undefined) {
          c.setLastNotePos(firstNote - c.width / 2);
          c.setX(firstNote - c.width / 2);
        }
      }
    });
  }
}

/**
 * Wrapper class to make NoteContainer implement IPlayable interface.
 */
class NoteContainerPlayable implements IPlayable {
  private container: NoteContainer;
  private currentState?: ProgressState;
  isFocused: boolean = false;

  constructor(container: NoteContainer) {
    this.container = container;
  }

  getPosition(): { x: number; y: number } {
    const bounds = this.container.getBounds();
    return {
      x: bounds?.x ?? 0,
      y: bounds?.y ?? 0,
    };
  }

  getState(): ProgressState | undefined {
    return this.currentState;
  }

  setTimingState(state: ProgressState): void {
    this.currentState = state;
    // Map ProgressState to NoteState
    let noteState: NoteState = 'default';
    switch (state) {
      case ProgressState.Ended:
        noteState = 'correct';
        break;
      case ProgressState.LateStart:
        noteState = 'late';
        break;
      case ProgressState.TimelyStart:
        noteState = 'correct';
        break;
      case ProgressState.Missed:
      case ProgressState.Missing:
        noteState = 'missed';
        break;
      case ProgressState.Waiting:
        noteState = this.isFocused ? 'focused' : 'default';
        break;
      default:
        noteState = 'default';
    }
    this.container.setState(noteState);
  }

  update(_cursorPosition: number): void {
    // NoteContainer handles its own updates via state changes
  }

  setFocus(focus: boolean): void {
    this.isFocused = focus;
    if (focus) {
      this.container.setState('focused');
    } else if (this.currentState === ProgressState.Waiting || this.currentState === undefined) {
      this.container.setState('default');
    }
  }

  setColor(color: string | number): void {
    // Convert color to appropriate format for NoteContainer
    const colorNum = typeof color === 'string' ? parseInt(color.replace('#', ''), 16) : color;
    this.container.setColor(colorNum);
  }

  pulse(): void {
    this.container.pulse();
    this.container.scalePop();
  }

  getHaloOffset(): number {
    return 0;
  }
}
